import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { ScrollArea } from '../ui/scroll-area';
import {
  X,
  MessageSquare,
  Send,
  Loader2,
  Trash2,
  MoreVertical,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { API_URL } from '../../config';

export default function InlineCommentsPanel({
  isOpen,
  onClose,
  documentId,
  comments,
  onCommentsChange,
  onHighlightComment,
  onCommentDeleted,
  currentUserId,
  /** Set when user clicks an inline mark in the editor — scroll + emphasize matching thread */
  focusedCommentId = null,
}) {
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [hoveredCommentId, setHoveredCommentId] = useState(null);

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    
    try {
      await axios.delete(`${API_URL}/documents/${documentId}/inline-comments/${commentId}`);
      onCommentsChange(comments.filter(c => c.id !== commentId));
      onCommentDeleted?.(commentId);
      toast.success('Comment deleted');
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  const handleResolveComment = async (commentId) => {
    try {
      const comment = comments.find(c => c.id === commentId);
      await axios.put(`${API_URL}/documents/${documentId}/inline-comments/${commentId}`, {
        is_resolved: !comment?.is_resolved
      });
      onCommentsChange(comments.map(c => 
        c.id === commentId ? { ...c, is_resolved: !c.is_resolved } : c
      ));
      toast.success(comment?.is_resolved ? 'Comment reopened' : 'Comment resolved');
    } catch (error) {
      toast.error('Failed to update comment');
    }
  };

  const handleMouseEnter = (commentId) => {
    setHoveredCommentId(commentId);
    onHighlightComment?.(commentId, true);
  };

  const handleMouseLeave = (commentId) => {
    setHoveredCommentId(null);
    onHighlightComment?.(commentId, false);
    if (focusedCommentId) {
      onHighlightComment?.(focusedCommentId, true);
    }
  };

  useEffect(() => {
    if (!isOpen || !focusedCommentId) return;
    const id = requestAnimationFrame(() => {
      const el = document.querySelector(`[data-inline-comment-card="${focusedCommentId}"]`);
      el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    });
    return () => cancelAnimationFrame(id);
  }, [isOpen, focusedCommentId, comments.length]);

  if (!isOpen) return null;

  const unresolvedComments = comments.filter(c => !c.is_resolved);
  const resolvedComments = comments.filter(c => c.is_resolved);

  return (
    <div className="fixed right-0 top-0 h-full w-80 bg-white dark:bg-slate-900 border-l border-slate-200 dark:border-slate-800 shadow-xl z-50 flex flex-col"
         data-testid="inline-comments-panel">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-800">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-blue-600" />
          <h3 className="font-semibold text-slate-900 dark:text-white">
            Comments
          </h3>
          {comments.length > 0 && (
            <span className="text-xs bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full text-slate-600 dark:text-slate-400">
              {comments.length}
            </span>
          )}
        </div>
        <Button variant="ghost" size="icon" onClick={onClose}>
          <X className="w-4 h-4" />
        </Button>
      </div>

      {/* Comments List */}
      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          {unresolvedComments.length === 0 && resolvedComments.length === 0 && (
            <div className="text-center py-8 text-slate-500">
              <MessageSquare className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              <p className="text-sm">No comments yet</p>
              <p className="text-xs mt-1">Select text and click Comment to add one</p>
            </div>
          )}

          {/* Unresolved Comments */}
          {unresolvedComments.map((comment) => (
            <div
              key={comment.id}
              data-inline-comment-card={comment.id}
              className={cn(
                "p-3 rounded-lg border transition-colors cursor-pointer",
                hoveredCommentId === comment.id || focusedCommentId === comment.id
                  ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-2 ring-blue-400/50"
                  : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
              )}
              onMouseEnter={() => handleMouseEnter(comment.id)}
              onMouseLeave={() => handleMouseLeave(comment.id)}
              data-testid={`comment-${comment.id}`}
            >
              {/* Quoted Text */}
              {comment.selected_text && (
                <div className="mb-2 pl-3 border-l-2 border-amber-400 text-sm text-slate-600 dark:text-slate-400 italic line-clamp-2">
                  "{comment.selected_text}"
                </div>
              )}

              {/* Comment Header */}
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Avatar className="w-6 h-6">
                    <AvatarFallback className="text-xs bg-blue-100 text-blue-700">
                      {getInitials(comment.author_name)}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-sm font-medium text-slate-900 dark:text-white">
                      {comment.author_name || 'Unknown'}
                    </p>
                    <p className="text-xs text-slate-400">
                      {new Date(comment.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="w-6 h-6">
                      <MoreVertical className="w-3 h-3" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleResolveComment(comment.id)}>
                      Mark as resolved
                    </DropdownMenuItem>
                    {(comment.author_id === currentUserId) && (
                      <DropdownMenuItem 
                        className="text-red-600"
                        onClick={() => handleDeleteComment(comment.id)}
                      >
                        Delete
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* Comment Text */}
              <p className="mt-2 text-sm text-slate-700 dark:text-slate-300">
                {comment.text}
              </p>
            </div>
          ))}

          {/* Resolved Comments */}
          {resolvedComments.length > 0 && (
            <>
              <div className="pt-4 pb-2 text-xs font-medium text-slate-400 uppercase tracking-wide">
                Resolved ({resolvedComments.length})
              </div>
              {resolvedComments.map((comment) => (
                <div
                  key={comment.id}
                  data-inline-comment-card={comment.id}
                  className={cn(
                    'p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50 opacity-60',
                    focusedCommentId === comment.id &&
                      'ring-2 ring-blue-400/50 border-blue-500 opacity-100 bg-blue-50/80 dark:bg-blue-900/30'
                  )}
                  data-testid={`comment-resolved-${comment.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Avatar className="w-5 h-5">
                        <AvatarFallback className="text-xs bg-slate-200 text-slate-600">
                          {getInitials(comment.author_name)}
                        </AvatarFallback>
                      </Avatar>
                      <p className="text-xs text-slate-500">{comment.author_name}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-xs"
                      onClick={() => handleResolveComment(comment.id)}
                    >
                      Reopen
                    </Button>
                  </div>
                  <p className="mt-1 text-xs text-slate-500 line-through">
                    {comment.text}
                  </p>
                </div>
              ))}
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
